// The sidebar's sections for the current context and permissions
// (web-frontend.md, Shell). Kept apart from the components so it can be
// unit-tested and shared.
import type { Route } from '../router';

export interface NavItem {
  label: string;
  route: Route;
  testId: string;
  /** Also selected on these routes (e.g. Sites while on a site page). */
  match: (r: Route) => boolean;
}

export interface NavSection {
  title?: string;
  testId?: string;
  items: NavItem[];
}

/** Any of these shows a site's Remote access page. */
export const REMOTE_PERMISSIONS = [
  'remote_access.web',
  'remote_access.terminal',
  'endpoint.write',
  'audit.read',
];

/** The sections for the current context and permissions (web-frontend.md, Shell). */
export function navSections(route: Route, permissions: string[], siteName?: string): NavSection[] {
  const sections: NavSection[] = [
    {
      items: [
        {
          label: 'Sites',
          route: { name: 'home' },
          testId: 'nav-sites',
          match: (r) => r.name === 'home',
        },
      ],
    },
  ];
  if ('siteId' in route) {
    const { siteId } = route;
    sections.push({
      title: siteName ?? 'Site',
      testId: 'nav-site-section',
      items: [
        {
          label: 'Control',
          route: { name: 'site', siteId },
          testId: 'nav-site-control',
          match: (r) => r.name === 'site',
        },
        {
          label: 'Schedules',
          route: { name: 'schedules', siteId },
          testId: 'nav-site-schedules',
          match: (r) => r.name === 'schedules',
        },
        {
          label: 'Telemetry',
          route: { name: 'telemetry', siteId },
          testId: 'nav-site-telemetry',
          match: (r) => r.name === 'telemetry',
        },
        ...(REMOTE_PERMISSIONS.some((p) => permissions.includes(p))
          ? [
              {
                label: 'Remote access',
                route: { name: 'remote', siteId } as Route,
                testId: 'nav-site-remote',
                match: (r: Route) => r.name === 'remote',
              },
            ]
          : []),
      ],
    });
  }
  const tenantItems: NavItem[] = [
    {
      label: 'Alerts',
      route: { name: 'alerts' },
      testId: 'nav-alerts',
      match: (r) => r.name === 'alerts',
    },
  ];
  if (permissions.includes('api_key.create')) {
    tenantItems.push({
      label: 'API keys',
      route: { name: 'apiKeys' },
      testId: 'nav-api-keys',
      match: (r) => r.name === 'apiKeys',
    });
  }
  if (tenantItems.length > 0) sections.push({ title: 'Tenant', items: tenantItems });
  return sections;
}
